import React, { useMemo } from "react";

import { containsHtml, interpolateTemplate, sanitizeHtml } from "../../../common/interpolateTemplate";

// Cached regex patterns for performance
const OPENING_BRACE_REGEX = /{{/g;
const CLOSING_BRACE_REGEX = /}}/g;

function interpolateTemplateForDisplayContent(
	fallbackText: string,
	template: string | undefined,
	context: Record<string, unknown>
): { text: string; isHtml: boolean; sanitizedHtml?: string } {
	// If we have a template, interpolate it with the context data
	// Example: "Call {{parameters.Operation.Name}}" becomes "Call GetUserData"
	// The 'context' object is made available as 'parameters' in the template expressions
	if (template) {
		// Augment the template with HTML spans with class param for parameters
		const templateAugmented = template.replace(OPENING_BRACE_REGEX, '<span class="param">{{').replace(CLOSING_BRACE_REGEX, "}}</span>");
		const interpolated = interpolateTemplate(templateAugmented, context) ?? templateAugmented;

		const isHtml = containsHtml(interpolated);

		return {
			text: interpolated,
			isHtml,
			sanitizedHtml: isHtml ? sanitizeHtml(interpolated) : undefined,
		};
	}

	// Fall back to the provided fallback text
	return {
		text: fallbackText,
		isHtml: false,
	};
}

export type DisplayContentTemplateProps = {
	/** The template string that may contain {{parameters.property}} expressions */
	template?: string;
	/** The fallback text to display if no template is provided */
	fallbackText: string;
	/** The context data that will be available as 'parameters' in template expressions */
	context: Record<string, unknown>;
	/** Additional CSS class name to apply to the wrapper span */
	className?: string;
};

/**
 * A reusable component that renders templated display content with support for HTML markup.
 *
 * Features:
 * - Interpolates template expressions like {{parameters.Operation.Name}}
 * - Automatically detects and safely renders HTML content
 * - Sanitizes HTML to prevent XSS attacks
 * - Falls back to plain text rendering when no HTML is detected
 * - Provides fallback text when no template is available
 *
 * @example
 * <DisplayContentTemplate
 *   template="<b>{{parameters.Operation.Name}}</b> on {{parameters.Service.Name}}"
 *   fallbackText="Default Action"
 *   context={{ Operation: { Name: 'GetData' }, Service: { Name: 'UserService' } }}
 *   className="activity-label"
 * />
 */
function DisplayContentTemplate({ template, fallbackText, context, className = "label" }: DisplayContentTemplateProps) {
	// Memoize all content processing (interpolation, HTML detection, and sanitization) to avoid expensive operations on every render
	const displayContent = useMemo(() => interpolateTemplateForDisplayContent(fallbackText, template, context), [template, fallbackText, context]);

	// Render the content based on whether it contains HTML
	if (displayContent.isHtml && displayContent.sanitizedHtml) {
		return <span className={className} data-testid="display-content-template" dangerouslySetInnerHTML={{ __html: displayContent.sanitizedHtml }} />;
	} else {
		// Render as plain text
		return (
			<span className={className} data-testid="display-content-template">
				{displayContent.text}
			</span>
		);
	}
}

export default DisplayContentTemplate;
